import { put, call, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { fetchMessages } from '../apis/chat';
import { errMessage } from '../../lib/apiError';
import {
  fetchLiveQueue,
  fetchIssueTrends,
  fetchAgentsMetric,
  fetchSupportArchive,
  fetchSupportOverview,
  deleteSupportConversation,
} from '../apis/supportConsole';
import {
  setArchivePage,
  setLiveQueuePage,
  setLiveQueueQueue,
  setArchiveFilters,
  getLiveQueueAction,
  getLiveQueueSuccess,
  getLiveQueueFailure,
  setArchiveThreadPage,
  getIssueTrendsAction,
  getAgentsMetricAction,
  getIssueTrendsSuccess,
  getIssueTrendsFailure,
  getAgentsMetricSuccess,
  getAgentsMetricFailure,
  getSupportArchiveAction,
  openArchiveThreadAction,
  getSupportOverviewAction,
  getSupportArchiveSuccess,
  getSupportArchiveFailure,
  openArchiveThreadSuccess,
  openArchiveThreadFailure,
  getSupportOverviewSuccess,
  getSupportOverviewFailure,
  deleteArchiveConversationAction,
  deleteArchiveConversationSuccess,
  deleteArchiveConversationFailure,
} from '../slices/supportConsole';

const selectArchive = (state) => state.supportConsole.archive;

function* handleOverview() {
  try {
    const response = yield call(fetchSupportOverview);
    yield put(getSupportOverviewSuccess(response));
  } catch (error) {
    yield put(getSupportOverviewFailure(errMessage(error, 'Could not load the queue summary')));
  }
}

function* handleLiveQueue() {
  try {
    const { queue, page } = yield select((state) => state.supportConsole.liveQueue);
    const response = yield call(fetchLiveQueue, { queue, page, limit: 50 });
    yield put(
      getLiveQueueSuccess({ items: response?.data || [], total: response?.total_records || 0 })
    );
  } catch (error) {
    yield put(getLiveQueueFailure(errMessage(error, 'Could not load the live queue')));
  }
}

function* handleArchive() {
  try {
    const { filters, page } = yield select(selectArchive);
    const response = yield call(fetchSupportArchive, { ...filters, page });
    yield put(
      getSupportArchiveSuccess({
        items: response?.data || [],
        total: response?.total_records || 0,
      })
    );
  } catch (error) {
    yield put(getSupportArchiveFailure(errMessage(error, 'Could not load the archive')));
  }
}

function* handleAgentsMetric() {
  try {
    const response = yield call(fetchAgentsMetric);
    // No page meta on this one, so the rows are all there is.
    yield put(getAgentsMetricSuccess(response?.data || []));
  } catch (error) {
    yield put(getAgentsMetricFailure(errMessage(error, 'Could not load agent metrics')));
  }
}

// The console serves no transcript, so the archive reads one through the agent
// surface - an admin reaches every thread there.
function* handleArchiveThread() {
  const { id: conversationId, page } = yield select((state) => state.supportConsole.transcript);
  if (!conversationId) return;
  try {
    const response = yield call(fetchMessages, conversationId, { page, limit: 50 });
    yield put(
      openArchiveThreadSuccess({
        id: conversationId,
        page,
        items: [...(response?.data || [])].reverse(),
        total: response?.total_records || 0,
      })
    );
  } catch (error) {
    yield put(
      openArchiveThreadFailure({
        id: conversationId,
        page,
        error: errMessage(error, 'Could not load the transcript'),
      })
    );
  }
}

function* handleDelete(action) {
  try {
    yield call(deleteSupportConversation, action.payload);
    yield put(deleteArchiveConversationSuccess(action.payload));
    const { page, total } = yield select((state) => state.supportConsole.archive);
    const lastPage = Math.max(1, Math.ceil(total / 50));
    yield put(page > lastPage ? setArchivePage(lastPage) : getSupportArchiveAction());
  } catch (error) {
    yield put(
      deleteArchiveConversationFailure(errMessage(error, 'Could not delete this conversation'))
    );
  }
}

function* handleIssues() {
  try {
    const response = yield call(fetchIssueTrends);
    yield put(getIssueTrendsSuccess(response));
  } catch (error) {
    yield put(getIssueTrendsFailure(errMessage(error, 'Could not load issue trends')));
  }
}

export default function* supportConsoleSaga() {
  yield takeLatest(getIssueTrendsAction.type, handleIssues);
  yield takeLatest([openArchiveThreadAction.type, setArchiveThreadPage.type], handleArchiveThread);
  yield takeLeading(deleteArchiveConversationAction.type, handleDelete);
  yield takeLatest(getSupportOverviewAction.type, handleOverview);
  yield takeLatest(
    [getLiveQueueAction.type, setLiveQueueQueue.type, setLiveQueuePage.type],
    handleLiveQueue
  );
  yield takeLatest(getAgentsMetricAction.type, handleAgentsMetric);
  // Changing a filter or page is the same request with new arguments.
  yield takeLatest(
    [getSupportArchiveAction.type, setArchiveFilters.type, setArchivePage.type],
    handleArchive
  );
}
